// ═══════════════════════════════════════════════════════════════
// people-directory.ts — Find-or-create + sync for person records
//
// Used by volunteer, intake, and roll-call flows to ensure every
// event-scoped record is linked to a canonical people record.
// Call findOrCreatePerson on create; call syncPersonFields to write
// new info back (non-destructively — never overwrites existing data).
// ═══════════════════════════════════════════════════════════════

import { queryRecords, createRecord, updateRecord, fetchRecord } from '../api/client';
import { TABLES } from '../api/tables';
import { FIELDS } from '../api/fields';

// ── Seed data pools (deterministic by index) ───────────────────
const SEED_STREETS = [
  '123 Oak Street', '456 Maple Avenue', '789 Pine Road', '321 Cedar Boulevard',
  '654 Elm Drive', '987 Birch Lane', '147 Willow Court', '258 Spruce Way',
  '369 Ash Place', '741 Cypress Terrace', '852 Sycamore Circle', '963 Poplar Street',
];
const SEED_CITIES = [
  { city: 'Sacramento',   state: 'CA', zip: '95814', county: 'Sacramento'   },
  { city: 'Fresno',       state: 'CA', zip: '93701', county: 'Fresno'       },
  { city: 'Bakersfield',  state: 'CA', zip: '93301', county: 'Kern'         },
  { city: 'Stockton',     state: 'CA', zip: '95202', county: 'San Joaquin'  },
  { city: 'Modesto',      state: 'CA', zip: '95354', county: 'Stanislaus'   },
  { city: 'Visalia',      state: 'CA', zip: '93291', county: 'Tulare'       },
  { city: 'Riverside',    state: 'CA', zip: '92501', county: 'Riverside'    },
  { city: 'San Bernardino', state: 'CA', zip: '92401', county: 'San Bernardino' },
  { city: 'Chico',        state: 'CA', zip: '95928', county: 'Butte'        },
  { city: 'Redding',      state: 'CA', zip: '96001', county: 'Shasta'       },
];
const SEED_EC_NAMES = [
  'James Wilson',    'Patricia Moore',  'Robert Taylor',  'Linda Anderson',
  'Michael Thomas',  'Barbara Jackson', 'David White',    'Susan Harris',
  'Richard Martin',  'Karen Thompson',  'Charles Garcia', 'Nancy Martinez',
];
const SEED_EC_RELATIONS = ['Spouse', 'Parent', 'Sibling', 'Child', 'Friend', 'Partner'];
const SEED_MEDICAL = [
  'Penicillin allergy',          'Diabetic — insulin dependent',
  'Asthma — carries inhaler',    'Severe nut allergy (EpiPen on person)',
  'Hypertension — on medication','Gluten intolerance',
  'Latex allergy',               'Heart condition — on blood thinners',
  'Seizure disorder',            'None known',
  'None known',                  'None known',  // weight toward "none"
];
const SEED_SKILLS = [
  'First Aid / CPR certified',   'Search & Rescue',
  'Medical — EMT',               'Amateur Radio (HAM) licensed',
  'Heavy Equipment Operator',    'Logistics & Supply Chain',
  'Communications & IT',         'Translation — Spanish/English',
  'Chainsaw certified',          'Structural Assessment',
  'Water Rescue',                'Animal Rescue & Care',
];
const SEED_CERTIFICATIONS = [
  'CPR/AED, First Aid',
  'ICS 100, ICS 200',
  'ICS 100, ICS 200, ICS 700',
  'CPR/AED, CERT Basic',
  'ICS 100, ICS 200, ICS 300, ICS 700',
  'Ham Radio (General class)',
  'Chainsaw Safety, CPR/AED',
  'Swift Water Rescue Level 1',
  'Wilderness First Aid',
  'ICS 100, CPR/AED, First Aid',
  'CERT Basic, ICS 100',
  'ICS 700, ICS 800',
];
const SEED_LANGUAGES = ['English', 'English, Spanish', 'English, Tagalog', 'English, Vietnamese', 'English, Armenian'];

function pick<T>(arr: T[], i: number): T { return arr[Math.abs(i) % arr.length]; }

function genPhone(seed: number): string {
  const area = 200 + (seed % 800);
  const mid  = 100 + ((seed * 7) % 900);
  const last = 1000 + ((seed * 13) % 9000);
  return `(${area}) ${mid}-${last}`;
}

function genDob(seed: number): string {
  // Age between 22 and 72
  const year = 2026 - 22 - (seed % 50);
  const month = 1 + (seed % 12);
  const day   = 1 + ((seed * 3) % 28);
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function genAvailabilityGrid(seed: number): Record<string, Record<string, boolean>> {
  const DAYS = ['mon','tue','wed','thu','fri','sat','sun'];
  const PERIODS = ['morning','afternoon','evening','overnight'];
  // Use seed to create realistic but varied patterns
  const patterns = [
    { days: ['mon','tue','wed','thu','fri'], periods: ['morning','afternoon'] },
    { days: ['mon','wed','fri','sat'],       periods: ['morning','afternoon','evening'] },
    { days: ['tue','thu','sat','sun'],       periods: ['morning','afternoon'] },
    { days: ['mon','tue','wed','thu','fri'], periods: ['morning','afternoon','evening'] },
    { days: ['sat','sun'],                  periods: ['morning','afternoon','evening','overnight'] },
    { days: ['mon','tue','wed','thu','fri','sat'], periods: ['morning','afternoon'] },
    { days: ['wed','thu','fri','sat','sun'], periods: ['morning','afternoon','evening'] },
  ];
  const pat = patterns[seed % patterns.length];
  const grid: Record<string, Record<string, boolean>> = {};
  for (const day of DAYS) {
    grid[day] = {};
    for (const period of PERIODS) {
      grid[day][period] = pat.days.includes(day) && pat.periods.includes(period);
    }
  }
  return grid;
}

function genEmail(firstName: string, lastName: string, seed: number): string {
  const domains = ['gmail.com', 'yahoo.com', 'outlook.com', 'icloud.com', 'hotmail.com'];
  const domain = pick(domains, seed);
  const fn = firstName.toLowerCase().replace(/[^a-z]/g, '');
  const ln = lastName.toLowerCase().replace(/[^a-z]/g, '');
  return `${fn}.${ln}${seed % 100}@${domain}`;
}

const FP = FIELDS.people;

export interface PersonLookupInput {
  firstName: string;
  lastName:  string;
  email?:    string;
  dob?:      string;
  phone?:    string;
}

export interface PersonSyncInput {
  email?:           string;
  phone?:           string;
  altPhone?:        string;
  ecName?:          string;
  ecPhone?:         string;
  ecRelation?:      string;
  skills?:          string;
  languages?:       string;
  street?:          string;
  city?:            string;
  state?:           string;
  zip?:             string;
  county?:          string;
  dob?:             string;
  specialNeeds?:    string;
  preferredLanguage?: string;
  availabilityNotes?: string;
}

// ── Helpers ────────────────────────────────────────────────────

function normalize(s: unknown): string {
  return String(s ?? '').trim().toLowerCase();
}

// Converts the volunteer availability grid JSON to a human-readable summary.
// Grid format: { mon: { morning: true, afternoon: false, evening: true, overnight: false }, ... }
// Output: "Mon (AM, Eve), Wed (AM, PM), Sat (All day)"
export function availabilityGridToText(jsonStr: string): string {
  if (!jsonStr) return '';
  let grid: Record<string, Record<string, boolean>>;
  try { grid = JSON.parse(jsonStr); } catch { return ''; }
  const DAY_ORDER = ['mon','tue','wed','thu','fri','sat','sun'];
  const DAY_LABEL: Record<string, string> = { mon:'Mon', tue:'Tue', wed:'Wed', thu:'Thu', fri:'Fri', sat:'Sat', sun:'Sun' };
  const PERIOD_LABEL: Record<string, string> = { morning:'AM', afternoon:'PM', evening:'Eve', overnight:'Night' };
  const PERIODS = ['morning','afternoon','evening','overnight'];
  const parts: string[] = [];
  for (const day of DAY_ORDER) {
    const dayGrid = grid[day];
    if (!dayGrid) continue;
    const on = PERIODS.filter(p => dayGrid[p]);
    if (on.length === 0) continue;
    if (on.length === PERIODS.length) {
      parts.push(`${DAY_LABEL[day]} (All day)`);
    } else {
      parts.push(`${DAY_LABEL[day]} (${on.map(p => PERIOD_LABEL[p]).join(', ')})`);
    }
  }
  return parts.join(', ');
}

// ── findOrCreatePerson ─────────────────────────────────────────
// Returns the record_id of the matching (or newly created) person.
// Match priority: email (most reliable) → firstName + lastName + DOB.
// If neither identifier is strong enough, creates a new record.

export async function findOrCreatePerson(
  data: PersonLookupInput,
): Promise<string> {
  if (!TABLES.people || !FP) return '';

  const email     = normalize(data.email);
  const firstName = data.firstName.trim();
  const lastName  = data.lastName.trim();
  const dob       = (data.dob ?? '').trim();

  // 1. Match by email
  if (email) {
    const res = await queryRecords<Record<string, unknown>>(TABLES.people, {
      filters: [{ field: FP.email, operator: 'eq', value: email }],
      limit: 1,
    });
    if (res.records.length > 0) {
      return String(res.records[0].record_id);
    }
  }

  // 2. Match by firstName + lastName + DOB (requires all three to avoid false matches)
  if (firstName && lastName && dob) {
    const res = await queryRecords<Record<string, unknown>>(TABLES.people, {
      filters: [
        { field: FP.firstName, operator: 'eq', value: firstName },
        { field: FP.lastName,  operator: 'eq', value: lastName  },
        { field: FP.dob,       operator: 'eq', value: dob       },
      ],
      limit: 1,
    });
    if (res.records.length > 0) {
      return String(res.records[0].record_id);
    }
  }

  // 3. Create new person record
  if (!firstName && !lastName) return ''; // refuse to create a blank record

  const payload: Record<string, unknown> = {
    [FP.firstName]: firstName,
    [FP.lastName]:  lastName,
  };
  if (email         && FP.email)    payload[FP.email]    = email;
  if (data.phone    && FP.phone)    payload[FP.phone]    = data.phone.trim();
  if (dob           && FP.dob)      payload[FP.dob]      = dob;
  if (FP.status)                    payload[FP.status]   = 'active';

  const created = await createRecord<Record<string, unknown>>(TABLES.people, payload);
  return String(created.record_id ?? created.id ?? '');
}

// ── syncPersonFields ───────────────────────────────────────────
// Non-destructively writes new info to an existing person record.
// Only patches fields that are currently blank on the person record.
// Safe to call after every create/update in any module.

export async function syncPersonFields(
  personId: string,
  data: PersonSyncInput,
): Promise<void> {
  if (!personId || !TABLES.people || !FP) return;

  const id = Number(personId);
  if (!id) return;

  let existing: Record<string, unknown> | null = null;
  try {
    existing = await fetchRecord<Record<string, unknown>>(TABLES.people, id);
  } catch { return; }
  if (!existing) return;

  const patch: Record<string, unknown> = {};

  function maybeSet(fieldKey: string, incoming: string | undefined) {
    if (!incoming?.trim()) return;
    const current = String(existing![fieldKey] ?? '').trim();
    if (!current) patch[fieldKey] = incoming.trim();
  }

  maybeSet(FP.email,            data.email);
  maybeSet(FP.phone,            data.phone);
  maybeSet(FP.altPhone,         data.altPhone);
  maybeSet(FP.ecName,           data.ecName);
  maybeSet(FP.ecPhone,          data.ecPhone);
  maybeSet(FP.ecRelation,       data.ecRelation);
  maybeSet(FP.skills,           data.skills);
  maybeSet(FP.languages,        data.languages);
  maybeSet(FP.street,           data.street);
  maybeSet(FP.city,             data.city);
  maybeSet(FP.state,            data.state);
  maybeSet(FP.zip,              data.zip);
  maybeSet(FP.county,           data.county);
  maybeSet(FP.dob,              data.dob);
  maybeSet(FP.specialNeeds,      data.specialNeeds);
  maybeSet(FP.preferredLanguage, data.preferredLanguage);
  maybeSet(FP.availabilityNotes, data.availabilityNotes);

  if (Object.keys(patch).length > 0) {
    await updateRecord(TABLES.people, id, patch);
  }
}

// ── seedDirectoryFromModules ───────────────────────────────────
// Fetches every volunteer and individual (intake/shelter), finds or
// creates their people record, then OVERWRITES the record with a full
// realistic data set so every field is populated for demo / QA.
// Calls onProgress(msg) after each record so the UI can show a spinner.

export interface SeedResult {
  created: number;
  enriched: number;
  errors: number;
}

export async function seedDirectoryFromModules(
  onProgress: (msg: string) => void,
): Promise<SeedResult> {
  const FV  = FIELDS.volunteers;
  const FI  = FIELDS.individuals;

  const result: SeedResult = { created: 0, enriched: 0, errors: 0 };
  let globalIdx = 0;

  // ── Helpers ───────────────────────────────────────────────────

  async function enrichPerson(personId: string, overrides: {
    firstName: string; lastName: string;
    email?: string; phone?: string;
    ecName?: string; ecPhone?: string;
    medicalNeeds?: string; skills?: string; type?: string;
    dob?: string; street?: string; city?: string; state?: string;
    zip?: string; county?: string;
  }, idx: number) {
    const numId = Number(personId);
    if (!numId) return;
    const loc = pick(SEED_CITIES, idx);
    const fullData: Record<string, unknown> = {
      [FP.firstName]: overrides.firstName,
      [FP.lastName]:  overrides.lastName,
      [FP.email]:     overrides.email || genEmail(overrides.firstName, overrides.lastName, idx),
      [FP.phone]:     overrides.phone || genPhone(idx * 3 + 1),
      [FP.altPhone]:  genPhone(idx * 7 + 5),
      [FP.street]:    overrides.street || pick(SEED_STREETS, idx),
      [FP.city]:      overrides.city   || loc.city,
      [FP.state]:     overrides.state  || loc.state,
      [FP.zip]:       overrides.zip    || loc.zip,
      [FP.county]:    overrides.county || loc.county,
      [FP.dob]:       overrides.dob    || genDob(idx * 11 + 3),
      [FP.ecName]:    overrides.ecName  || pick(SEED_EC_NAMES,      idx),
      [FP.ecPhone]:   overrides.ecPhone || genPhone(idx * 5 + 2),
      [FP.ecRelation]:pick(SEED_EC_RELATIONS, idx),
      [FP.specialNeeds]: overrides.medicalNeeds || pick(SEED_MEDICAL, idx),
      [FP.skills]:    overrides.skills || pick(SEED_SKILLS, idx),
      [FP.languages]: pick(SEED_LANGUAGES, idx),
      [FP.status]:    'active',
    };
    if (overrides.type) fullData[FP.type] = overrides.type;
    await updateRecord(TABLES.people, numId, fullData);
  }

  // ── Seed from Volunteers ──────────────────────────────────────
  if (TABLES.volunteers && FV) {
    let offset = 0;
    const limit = 200;
    let fetched = 0;
    do {
      let res: Awaited<ReturnType<typeof queryRecords>>;
      try {
        res = await queryRecords<Record<string, unknown>>(TABLES.volunteers, {
          limit, offset,
          sort: { field: 'created_at', direction: 'desc' },
        });
      } catch { break; }
      fetched = res.records.length;
      for (const r of res.records) {
        const firstName = String(r[FV.firstName] ?? '').trim();
        const lastName  = String(r[FV.lastName]  ?? '').trim();
        if (!firstName && !lastName) continue;
        const idx = globalIdx++;
        onProgress(`Volunteers: ${firstName} ${lastName}…`);
        try {
          const personId = await findOrCreatePerson({
            firstName, lastName,
            email: String(r[FV.email] ?? ''),
            phone: String(r[FV.phone] ?? ''),
          });
          if (!personId) { result.errors++; continue; }
          await enrichPerson(personId, {
            firstName, lastName,
            email:       String(r[FV.email]           ?? ''),
            phone:       String(r[FV.phone]           ?? ''),
            ecName:      String(r[FV.ecName]          ?? r[FV.emergencyContact] ?? ''),
            ecPhone:     String(r[FV.ecPhone]         ?? r[FV.emergencyPhone]   ?? ''),
            skills:      String(r[FV.skills]          ?? ''),
            street:      String(r[FV.address]         ?? ''),
            city:        String(r[FV.city]            ?? ''),
            state:       String(r[FV.state]           ?? ''),
            zip:         String(r[FV.zip]             ?? ''),
            type: 'volunteer',
          }, idx);
          result.enriched++;
        } catch { result.errors++; }
      }
      offset += fetched;
    } while (fetched === limit);
  }

  // ── Seed from Individuals (Intake / Shelter) ──────────────────
  if (TABLES.individuals && FI) {
    let offset = 0;
    const limit = 200;
    let fetched = 0;
    do {
      let res: Awaited<ReturnType<typeof queryRecords>>;
      try {
        res = await queryRecords<Record<string, unknown>>(TABLES.individuals, {
          limit, offset,
          sort: { field: 'created_at', direction: 'desc' },
        });
      } catch { break; }
      fetched = res.records.length;
      for (const r of res.records) {
        const firstName = String(r[FI.firstName] ?? '').trim();
        const lastName  = String(r[FI.lastName]  ?? '').trim();
        if (!firstName && !lastName) continue;
        const idx = globalIdx++;
        onProgress(`Intake: ${firstName} ${lastName}…`);
        try {
          // Use existing personId FK if already set
          const existingPersonId = FI.personId ? String(r[FI.personId] ?? '') : '';
          const personId = existingPersonId || await findOrCreatePerson({
            firstName, lastName,
            email: String(r[FI.email] ?? ''),
            dob:   String(r[FI.dateOfBirth] ?? ''),
            phone: String(r[FI.phone] ?? ''),
          });
          if (!personId) { result.errors++; continue; }
          // Write personId FK back to individual record if not set
          if (!existingPersonId && FI.personId) {
            const rid = Number(r.record_id);
            if (rid) {
              updateRecord(TABLES.individuals, rid, { [FI.personId]: personId }).catch(() => {});
            }
          }
          await enrichPerson(personId, {
            firstName, lastName,
            email:        String(r[FI.email]                ?? ''),
            phone:        String(r[FI.phone]                ?? ''),
            ecName:       String(r[FI.emergencyContactName] ?? ''),
            ecPhone:      String(r[FI.emergencyContactPhone]?? ''),
            medicalNeeds: String(r[FI.medicalNeeds]         ?? ''),
            dob:          String(r[FI.dateOfBirth]          ?? ''),
            street:       String(r[FI.address]              ?? ''),
            city:         String(r[FI.city]                 ?? ''),
            state:        String(r[FI.state]                ?? ''),
            zip:          String(r[FI.zip]                  ?? ''),
            type: 'registrant',
          }, idx);
          result.enriched++;
        } catch { result.errors++; }
      }
      offset += fetched;
    } while (fetched === limit);
  }

  return result;
}

// ── seedAllPeopleRecords ───────────────────────────────────────
// Walks EVERY record in the people table directly (no source-table
// join needed) and fills in any blank fields with realistic fake data.
// This catches everyone regardless of how they were created.

export async function seedAllPeopleRecords(
  onProgress: (done: number, total: number, name: string) => void,
): Promise<SeedResult> {
  if (!TABLES.people || !FP) return { created: 0, enriched: 0, errors: 0 };

  const result: SeedResult = { created: 0, enriched: 0, errors: 0 };
  const limit = 200;
  let offset = 0;
  let globalIdx = 0;
  let totalSeen = 0;

  // First pass: count total for progress display
  let grandTotal = 0;
  try {
    const probe = await queryRecords<Record<string, unknown>>(TABLES.people, { limit: 1, offset: 0 });
    grandTotal = probe.total ?? 0;
  } catch { /* ignore */ }

  let fetched = 0;
  do {
    let res: Awaited<ReturnType<typeof queryRecords>>;
    try {
      res = await queryRecords<Record<string, unknown>>(TABLES.people, {
        limit, offset,
        sort: { field: FP.lastName, direction: 'asc' },
      });
    } catch { break; }
    fetched = res.records.length;

    for (const r of res.records as Record<string, unknown>[]) {
      const firstName = String(r[FP.firstName] ?? '').trim();
      const lastName  = String(r[FP.lastName]  ?? '').trim();
      if (!firstName && !lastName) { globalIdx++; totalSeen++; continue; }

      const idx = globalIdx++;
      totalSeen++;
      onProgress(totalSeen, grandTotal || totalSeen, `${firstName} ${lastName}`);

      // Build patch — only fill fields that are currently blank
      const patch: Record<string, unknown> = {};
      const has = (key: string) => String(r[key] ?? '').trim().length > 0;
      const loc = pick(SEED_CITIES, idx);

      if (!has(FP.email))      patch[FP.email]      = genEmail(firstName, lastName, idx);
      if (!has(FP.phone))      patch[FP.phone]      = genPhone(idx * 3 + 1);
      if (!has(FP.altPhone))   patch[FP.altPhone]   = genPhone(idx * 7 + 5);
      if (!has(FP.street))     patch[FP.street]     = pick(SEED_STREETS, idx);
      if (!has(FP.city))       patch[FP.city]       = loc.city;
      if (!has(FP.state))      patch[FP.state]      = loc.state;
      if (!has(FP.zip))        patch[FP.zip]        = loc.zip;
      if (!has(FP.county))     patch[FP.county]     = loc.county;
      if (!has(FP.dob))        patch[FP.dob]        = genDob(idx * 11 + 3);
      if (!has(FP.ecName))     patch[FP.ecName]     = pick(SEED_EC_NAMES, idx);
      if (!has(FP.ecPhone))    patch[FP.ecPhone]    = genPhone(idx * 5 + 2);
      if (!has(FP.ecRelation)) patch[FP.ecRelation] = pick(SEED_EC_RELATIONS, idx);
      if (!has(FP.specialNeeds)) patch[FP.specialNeeds] = pick(SEED_MEDICAL, idx);
      if (!has(FP.skills))     patch[FP.skills]     = pick(SEED_SKILLS, idx);
      if (!has(FP.languages))  patch[FP.languages]  = pick(SEED_LANGUAGES, idx);

      if (Object.keys(patch).length === 0) continue; // already complete

      const numId = Number(r.record_id);
      if (!numId) continue;
      try {
        await updateRecord(TABLES.people, numId, patch);
        result.enriched++;
      } catch { result.errors++; }
    }
    offset += fetched;
  } while (fetched === limit);

  return result;
}

// ── seedVolunteerRecords ───────────────────────────────────────
// Fills blank fields on every volunteer record directly.

export async function seedVolunteerRecords(
  onProgress: (done: number, name: string) => void,
): Promise<SeedResult> {
  const FV = FIELDS.volunteers;
  if (!TABLES.volunteers || !FV) return { created: 0, enriched: 0, errors: 0 };

  const result: SeedResult = { created: 0, enriched: 0, errors: 0 };
  let offset = 0;
  const limit = 200;
  let fetched = 0;
  let idx = 0;

  do {
    let res: Awaited<ReturnType<typeof queryRecords>>;
    try {
      res = await queryRecords<Record<string, unknown>>(TABLES.volunteers, {
        limit, offset, sort: { field: 'created_at', direction: 'desc' },
      });
    } catch { break; }
    fetched = res.records.length;

    for (const r of res.records as Record<string, unknown>[]) {
      const firstName = String(r[FV.firstName] ?? '').trim();
      const lastName  = String(r[FV.lastName]  ?? '').trim();
      if (!firstName && !lastName) { idx++; continue; }
      const i = idx++;
      onProgress(i + 1, `${firstName} ${lastName}`);

      const has = (k: string) => String(r[k] ?? '').trim().length > 0;
      const patch: Record<string, unknown> = {};
      const loc = pick(SEED_CITIES, i);

      if (!has(FV.email)            && FV.email)            patch[FV.email]            = genEmail(firstName, lastName, i);
      if (!has(FV.phone)            && FV.phone)            patch[FV.phone]            = genPhone(i * 3 + 1);
      if (!has(FV.address)          && FV.address)          patch[FV.address]          = pick(SEED_STREETS, i);
      if (!has(FV.city)             && FV.city)             patch[FV.city]             = loc.city;
      if (!has(FV.state)            && FV.state)            patch[FV.state]            = loc.state;
      if (!has(FV.zip)              && FV.zip)              patch[FV.zip]              = loc.zip;
      if (!has(FV.skills)           && FV.skills)           patch[FV.skills]           = pick(SEED_SKILLS, i);
      if (!has(FV.certifications)   && FV.certifications)   patch[FV.certifications]   = pick(SEED_CERTIFICATIONS, i);
      if (!has(FV.emergencyContact) && FV.emergencyContact) patch[FV.emergencyContact] = pick(SEED_EC_NAMES, i);
      if (!has(FV.emergencyPhone)   && FV.emergencyPhone)   patch[FV.emergencyPhone]   = genPhone(i * 5 + 2);
      if (!has(FV.ecName)           && FV.ecName)           patch[FV.ecName]           = pick(SEED_EC_NAMES, i);
      if (!has(FV.ecPhone)          && FV.ecPhone)          patch[FV.ecPhone]          = genPhone(i * 5 + 2);

      // Enrich the notes JSON extras (dob, dietary/allergies, languages, ecRelationship, waiver)
      const existingNotes = String(r[FV.notes] ?? '');
      let extras: Record<string, unknown> = {};
      try { extras = JSON.parse(existingNotes); } catch { /* ok */ }
      let notesChanged = false;
      if (!extras.dob)            { extras.dob = genDob(i * 11 + 3); notesChanged = true; }
      if (!extras.dietary)        { extras.dietary = pick(SEED_MEDICAL, i); notesChanged = true; }
      if (!extras.languages)      { extras.languages = pick(SEED_LANGUAGES, i); notesChanged = true; }
      if (!extras.ecRelationship) { extras.ecRelationship = pick(SEED_EC_RELATIONS, i); notesChanged = true; }
      if (!extras.waiverAccepted) { extras.waiverAccepted = true; extras.waiverDate = genDob(i).slice(0, 7) + '-01'; notesChanged = true; }

      // Always generate a proper grid — replace the old string values ('both', 'active', etc.)
      const rawAvail = String(r[FV.availability] ?? extras.availability ?? '');
      const hasProperGrid = rawAvail.startsWith('{');
      if (!hasProperGrid) {
        const grid = genAvailabilityGrid(i);
        patch[FV.availability] = JSON.stringify(grid);
        extras.availability = JSON.stringify(grid);
        notesChanged = true;
      }
      if (notesChanged) patch[FV.notes] = JSON.stringify(extras);

      // Find/create the people record and write ALL data to it
      const email = String(r[FV.email] ?? patch[FV.email] ?? '');
      const phone = String(r[FV.phone] ?? patch[FV.phone] ?? '');
      const dob   = String(extras.dob ?? '');
      try {
        let personId = FV.personId ? String(r[FV.personId] ?? '') : '';
        if (!personId) {
          personId = await findOrCreatePerson({ firstName, lastName, email, phone, dob });
          if (personId && FV.personId) patch[FV.personId] = personId;
        }
        if (personId) {
          const availJson = String(patch[FV.availability] ?? r[FV.availability] ?? '');
          const availText = availabilityGridToText(availJson);
          await syncPersonFields(personId, {
            email,
            phone,
            ecName:            String(r[FV.ecName] ?? patch[FV.ecName] ?? extras.ecName ?? ''),
            ecPhone:           String(r[FV.ecPhone] ?? patch[FV.ecPhone] ?? extras.ecPhone ?? ''),
            ecRelation:        String(extras.ecRelationship ?? ''),
            skills:            String(r[FV.skills] ?? patch[FV.skills] ?? ''),
            languages:         String(extras.languages ?? ''),
            specialNeeds:      String(extras.dietary ?? ''),
            dob,
            street:            String(r[FV.address] ?? patch[FV.address] ?? ''),
            city:              String(r[FV.city]    ?? patch[FV.city]    ?? ''),
            state:             String(r[FV.state]   ?? patch[FV.state]   ?? ''),
            zip:               String(r[FV.zip]     ?? patch[FV.zip]     ?? ''),
            availabilityNotes: availText,
          });
        }
      } catch { /* don't block the volunteer record update */ }

      if (Object.keys(patch).length === 0) continue;
      const numId = Number(r.record_id);
      if (!numId) continue;
      try {
        await updateRecord(TABLES.volunteers, numId, patch);
        result.enriched++;
      } catch { result.errors++; }
    }
    offset += fetched;
  } while (fetched === limit);

  return result;
}

// ── seedIndividualRecords ──────────────────────────────────────
// Fills blank fields on every intake/shelter individual record.

export async function seedIndividualRecords(
  onProgress: (done: number, name: string) => void,
): Promise<SeedResult> {
  const FI = FIELDS.individuals;
  if (!TABLES.individuals || !FI) return { created: 0, enriched: 0, errors: 0 };

  const result: SeedResult = { created: 0, enriched: 0, errors: 0 };
  let offset = 0;
  const limit = 200;
  let fetched = 0;
  let idx = 0;

  do {
    let res: Awaited<ReturnType<typeof queryRecords>>;
    try {
      res = await queryRecords<Record<string, unknown>>(TABLES.individuals, {
        limit, offset, sort: { field: 'created_at', direction: 'desc' },
      });
    } catch { break; }
    fetched = res.records.length;

    for (const r of res.records as Record<string, unknown>[]) {
      const firstName = String(r[FI.firstName] ?? '').trim();
      const lastName  = String(r[FI.lastName]  ?? '').trim();
      if (!firstName && !lastName) { idx++; continue; }
      const i = idx++;
      onProgress(i + 1, `${firstName} ${lastName}`);

      const has = (k: string) => String(r[k] ?? '').trim().length > 0;
      const patch: Record<string, unknown> = {};
      const loc = pick(SEED_CITIES, i);

      if (!has(FI.phone)                 && FI.phone)                patch[FI.phone]                = genPhone(i * 3 + 1);
      if (!has(FI.email)                 && FI.email)                patch[FI.email]                = genEmail(firstName, lastName, i);
      if (!has(FI.address)               && FI.address)              patch[FI.address]              = pick(SEED_STREETS, i);
      if (!has(FI.city)                  && FI.city)                 patch[FI.city]                 = loc.city;
      if (!has(FI.state)                 && FI.state)                patch[FI.state]                = loc.state;
      if (!has(FI.zip)                   && FI.zip)                  patch[FI.zip]                  = loc.zip;
      if (!has(FI.dateOfBirth)           && FI.dateOfBirth)          patch[FI.dateOfBirth]          = genDob(i * 11 + 3);
      if (!has(FI.emergencyContactName)  && FI.emergencyContactName) patch[FI.emergencyContactName] = pick(SEED_EC_NAMES, i);
      if (!has(FI.emergencyContactPhone) && FI.emergencyContactPhone)patch[FI.emergencyContactPhone]= genPhone(i * 5 + 2);
      if (!has(FI.medicalNeeds)          && FI.medicalNeeds)         patch[FI.medicalNeeds]         = pick(SEED_MEDICAL, i);
      if (!has(FI.gender)                && FI.gender)               patch[FI.gender]               = i % 2 === 0 ? 'Male' : 'Female';

      if (Object.keys(patch).length === 0) continue;
      const numId = Number(r.record_id);
      if (!numId) continue;
      try {
        await updateRecord(TABLES.individuals, numId, patch);
        result.enriched++;
      } catch { result.errors++; }
    }
    offset += fetched;
  } while (fetched === limit);

  return result;
}

// ── seedSurvivorRecords ────────────────────────────────────────
// Fills blank fields on every shelter survivor record and also
// back-fills the personId FK + syncs data to the people table.

export async function seedSurvivorRecords(
  onProgress: (done: number, name: string) => void,
): Promise<SeedResult> {
  const FSv = FIELDS.survivors;
  if (!TABLES.survivors || !FSv) return { created: 0, enriched: 0, errors: 0 };

  const result: SeedResult = { created: 0, enriched: 0, errors: 0 };
  let offset = 0;
  const limit = 200;
  let fetched = 0;
  let idx = 0;

  do {
    let res: Awaited<ReturnType<typeof queryRecords>>;
    try {
      res = await queryRecords<Record<string, unknown>>(TABLES.survivors, {
        limit, offset, sort: { field: 'created_at', direction: 'desc' },
      });
    } catch { break; }
    fetched = res.records.length;

    for (const r of res.records as Record<string, unknown>[]) {
      const firstName = String(r[FSv.firstName] ?? '').trim();
      const lastName  = String(r[FSv.lastName]  ?? '').trim();
      if (!firstName && !lastName) { idx++; continue; }
      const i = idx++;
      onProgress(i + 1, `${firstName} ${lastName}`);

      const has = (k: string) => String(r[k] ?? '').trim().length > 0;
      const patch: Record<string, unknown> = {};
      const loc = pick(SEED_CITIES, i);

      if (!has(FSv.phone)                 && FSv.phone)                patch[FSv.phone]                = genPhone(i * 3 + 1);
      if (!has(FSv.email)                 && FSv.email)                patch[FSv.email]                = genEmail(firstName, lastName, i);
      if (!has(FSv.address)               && FSv.address)              patch[FSv.address]              = pick(SEED_STREETS, i);
      if (!has(FSv.city)                  && FSv.city)                 patch[FSv.city]                 = loc.city;
      if (!has(FSv.state)                 && FSv.state)                patch[FSv.state]                = loc.state;
      if (!has(FSv.zip)                   && FSv.zip)                  patch[FSv.zip]                  = loc.zip;
      if (!has(FSv.dateOfBirth)           && FSv.dateOfBirth)          patch[FSv.dateOfBirth]          = genDob(i * 11 + 3);
      if (!has(FSv.emergencyContactName)  && FSv.emergencyContactName) patch[FSv.emergencyContactName] = pick(SEED_EC_NAMES, i);
      if (!has(FSv.emergencyContactPhone) && FSv.emergencyContactPhone)patch[FSv.emergencyContactPhone]= genPhone(i * 5 + 2);
      if (!has(FSv.medicalNeeds)          && FSv.medicalNeeds)         patch[FSv.medicalNeeds]         = pick(SEED_MEDICAL, i);
      if (!has(FSv.dietaryNeeds)          && FSv.dietaryNeeds)         patch[FSv.dietaryNeeds]         = i % 3 === 0 ? 'Gluten-free' : i % 3 === 1 ? 'Vegetarian' : 'No restrictions';
      if (!has(FSv.gender)                && FSv.gender)               patch[FSv.gender]               = i % 2 === 0 ? 'Male' : 'Female';

      // Find/create the people record and back-fill personId FK
      const existingPersonId = FSv.personId ? String(r[FSv.personId] ?? '') : '';
      if (!existingPersonId && FSv.personId) {
        try {
          const personId = await findOrCreatePerson({
            firstName, lastName,
            email: String(r[FSv.email] ?? patch[FSv.email] ?? ''),
            dob:   String(r[FSv.dateOfBirth] ?? patch[FSv.dateOfBirth] ?? ''),
            phone: String(r[FSv.phone] ?? patch[FSv.phone] ?? ''),
          });
          if (personId) {
            patch[FSv.personId] = personId;
            // Sync all available data to the people record
            syncPersonFields(personId, {
              email:        String(r[FSv.email]                ?? patch[FSv.email]                ?? ''),
              phone:        String(r[FSv.phone]                ?? patch[FSv.phone]                ?? ''),
              ecName:       String(r[FSv.emergencyContactName] ?? patch[FSv.emergencyContactName] ?? ''),
              ecPhone:      String(r[FSv.emergencyContactPhone]?? patch[FSv.emergencyContactPhone]?? ''),
              specialNeeds: String(r[FSv.medicalNeeds]         ?? patch[FSv.medicalNeeds]         ?? ''),
              dob:          String(r[FSv.dateOfBirth]          ?? patch[FSv.dateOfBirth]          ?? ''),
              street:       String(r[FSv.address]              ?? patch[FSv.address]              ?? ''),
              city:         String(r[FSv.city]                 ?? patch[FSv.city]                 ?? ''),
              state:        String(r[FSv.state]                ?? patch[FSv.state]                ?? ''),
              zip:          String(r[FSv.zip]                  ?? patch[FSv.zip]                  ?? ''),
            }).catch(() => {});
          }
        } catch { /* continue */ }
      }

      if (Object.keys(patch).length === 0) continue;
      const numId = Number(r.record_id);
      if (!numId) continue;
      try {
        await updateRecord(TABLES.survivors, numId, patch);
        result.enriched++;
      } catch { result.errors++; }
    }
    offset += fetched;
  } while (fetched === limit);

  return result;
}
